<template>
    <AcademyLayout>
        <main class="container-fluid p-0">
            <div class="courses-container">
                <h2 class="courses-title">{{ isTutor ? $t('academy.my-groups')+':' : $t('navbar.courses')+':' }}</h2>
                <div v-if="isLoading" class="text-center">
                    <b-spinner label="Cargando..."></b-spinner>
                </div>
                <CoursesGrid 
                    v-else
                    :courses="courses" 
                    context="groups" 
                    @course-selected="handleCourseSelection" 
                />
            </div>

            <!-- Pop-up para mostrar los grupos en lista desplegable -->
            <transition name="slide-fade">
                <div v-if="showPopup" class="popup-overlay" @click.self="closePopup">
                    <div class="popup-content">
                        <span class="close-icon" @click="closePopup">×</span>

                        <div class="course-info">
                            <label class="label-title">{{ $t('academy.premium_course') }}:</label>
                            <input type="text" class="course-name" :value="selectedCourse.name" disabled>
                        </div>

                        <div class="dropdown-container">
                            <label class="label-title">{{ $t('academy.groups') }}:</label>
                            <b-dropdown :text="$t('academy.view_groups')" class="dropdown-groups">
                                <b-dropdown-item v-for="group in selectedGroups" :key="group.id"
                                    @click="goToGroup(group)">
                                    {{ group.schedule }}
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>
                </div>
            </transition>
        </main>
    </AcademyLayout>
</template>

<script>
import AcademyLayout from '@/layouts/AcademyLayout.vue';
import CoursesGrid from "@/components/academy/CoursesGrid.vue";
import academyData from '@/data/academyData';
import { getAuth } from 'firebase/auth';

export default {
    components: {
        AcademyLayout,
        CoursesGrid
    },
    data() {
        return {
            courses: [],
            groups: [],
            selectedCourse: null,
            selectedGroups: [],
            showPopup: false,
            isLoading: true,
            isTutor: false
        };
    },

    async mounted() {
        this.isLoading = true;
        try {
            const auth = getAuth();
            
            // Esperar a que Firebase Auth inicialice
            if (!auth.currentUser) {
                await new Promise((resolve) => {
                    const unsubscribe = auth.onAuthStateChanged((user) => {
                        unsubscribe();
                        resolve(user);
                    });
                });
            }

            // Verificar si el usuario está autenticado
            if (!auth.currentUser) {
                console.error("Usuario no autenticado");
                this.$router.push('/login');
                return;
            }

            const userId = auth.currentUser.uid;

            // Intentar obtener grupos como tutor
            let tutorGroups = await academyData.getGroupsByTutorId(userId);
            
            // Verificar si el usuario es tutor
            this.isTutor = tutorGroups.length > 0;
            
            // Intentar obtener grupos como estudiante
            let studentGroups = await academyData.getGroupsByStudentId(userId);

            // Combinar los grupos y eliminar duplicados si los hubiera
            this.groups = [...tutorGroups, ...studentGroups]
                .filter((group, index, self) => 
                    index === self.findIndex(g => g.id === group.id)
                );

            if (!this.groups || !this.groups.length) {
                console.log("No se encontraron grupos para este usuario");
                this.groups = [];
                return;
            }

            // Filtrar grupos que tengan un curso válido
            this.groups = this.groups.filter(group => {
                if (!group.course || !group.course.uid) {
                    console.warn(`Grupo ${group.id} no tiene un curso válido:`, group.course);
                    return false;
                }
                return true;
            });

            if (!this.groups.length) {
                console.log("No se encontraron grupos con cursos válidos");
                this.groups = [];
                return;
            }

            // Obtener los cursos únicos basados en los grupos
            const courseIds = [...new Set(this.groups.map(group => 
                group.course?.uid).filter(Boolean))];

            if (courseIds.length) {
                // Usar Promise.allSettled para manejar errores individuales
                const coursePromises = courseIds.map(async (courseId) => {
                    try {
                        return await academyData.getCourseById(courseId);
                    } catch (error) {
                        console.warn(`Curso no encontrado con ID: ${courseId}`, error);
                        return null;
                    }
                });
                
                const courseResults = await Promise.all(coursePromises);
                this.courses = courseResults.filter(course => course !== null);
            }

        } catch (error) {
            console.error("Error al cargar grupos y cursos:", error);
            this.groups = [];
            this.courses = [];
        } finally {
            this.isLoading = false;
        }
    },

    methods: {
        handleCourseSelection(courseId) {
            this.selectedCourse = this.courses.find(course => course.id === courseId);
            this.selectedGroups = this.groups.filter(group => group.course.uid === courseId);
            
            // Si no es tutor y solo tiene un grupo, ir directamente
            if (!this.isTutor && this.selectedGroups.length === 1) {
                this.goToGroup(this.selectedGroups[0]);
            } else {
                this.showPopup = true;
            }
        },

        closePopup() {
            this.showPopup = false;
        },

        async goToGroup(group) {
            const auth = getAuth();
            const userId = auth.currentUser.uid;
            
            // Verificar si el usuario actual es un estudiante del grupo
            const isStudent = group.students?.some(student => student.uid === userId);
            
            if (isStudent) {
                // Si es estudiante, ir directamente a su backoffice
                this.$router.push({
                    name: "student-backoffice",
                    params: {
                        lang: this.$i18n.locale,
                        studentId: userId
                    },
                    query: {
                        groupId: group.id,
                        studentName: group.students.find(s => s.uid === userId)?.name || 'Estudiante'
                    }
                });
            } else {
                // Si es tutor, ir a la vista de calificaciones con la ruta simplificada
                this.$router.push({
                    name: "grades",
                    params: {
                        lang: this.$i18n.locale,
                        groupId: group.id
                    }
                });
            }
        }
    }
};
</script>